/**
 * Admin Booking Detail Page
 *
 * The bookings list has always linked "View" here, but the page was never
 * built, so every link 404'd. It also hosts the only entry point to
 * markManualPayment: guests paying by Whish/wire flag the transfer with
 * flagManualPaymentSent, and an admin confirms the money actually arrived
 * here, which marks the booking paid and records the host's earning.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import { AdminLayout } from '../layout';
import { useAdminBooking } from './hooks';
import toast from 'react-hot-toast';

type ManualMethod = 'whish_money' | 'wire_transfer';

const formatCurrency = (n: number) => `$${n.toFixed(2)}`;
const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  refunded: 'bg-purple-100 text-purple-700',
  partial_refund: 'bg-purple-100 text-purple-700',
};

export const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { booking, isLoading } = useAdminBooking(bookingId || '');

  const [method, setMethod] = useState<ManualMethod>('whish_money');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Reflect the change immediately: useAdminBooking is a one-shot read.
  const [paidNow, setPaidNow] = useState(false);

  const confirmPayment = async () => {
    if (!bookingId) return;
    setSubmitting(true);
    try {
      const markPaid = httpsCallable<
        { bookingId: string; method: ManualMethod; reference?: string },
        { success: boolean }
      >(functions, 'markManualPayment');
      await markPaid({
        bookingId,
        method,
        reference: reference.trim() || undefined,
      });
      setPaidNow(true);
      toast.success('Payment confirmed. The booking is now marked paid.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm payment';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-64" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!booking) {
    return (
      <AdminLayout>
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking not found</h1>
          <Link to="/admin/bookings" className="text-primary-600 hover:underline">
            Back to bookings
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const paymentStatus = paidNow ? 'paid' : booking.paymentStatus;
  const isPaid = paymentStatus === 'paid';

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl">
        <Link
          to="/admin/bookings"
          className="text-sm text-gray-500 hover:text-gray-900 mb-4 inline-block"
        >
          ← Back to bookings
        </Link>

        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{booking.listingTitle}</h1>
            <p className="text-sm text-gray-500 font-mono mt-1">#{booking.id}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[booking.status] || ''}`}>
              {booking.status}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[paymentStatus] || ''}`}>
              {isPaid ? 'paid' : `payment ${paymentStatus}`}
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            {/* Trip */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Trip</h2>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Check-in</dt>
                  <dd className="font-medium">{formatDate(booking.checkIn)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Check-out</dt>
                  <dd className="font-medium">{formatDate(booking.checkOut)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Nights</dt>
                  <dd className="font-medium">{booking.nights}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Guests</dt>
                  <dd className="font-medium">
                    {booking.guests.adults + booking.guests.children}
                  </dd>
                </div>
              </dl>
            </section>

            {/* People */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">People</h2>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 mb-1">Guest</p>
                  <p className="font-medium">{booking.guestName}</p>
                  <p className="text-gray-500">{booking.guestEmail}</p>
                  {booking.guestPhone && <p className="text-gray-500">{booking.guestPhone}</p>}
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Host</p>
                  <p className="font-medium">{booking.hostName}</p>
                  <p className="text-gray-500">{booking.hostEmail}</p>
                </div>
              </div>
            </section>

            {/* Money */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-lg mb-4">Payment breakdown</h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">
                    {formatCurrency(booking.pricing.nightlyRate)} × {booking.nights} nights
                  </dt>
                  <dd>{formatCurrency(booking.pricing.nightlyTotal)}</dd>
                </div>
                {booking.pricing.cleaningFee > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Cleaning fee</dt>
                    <dd>{formatCurrency(booking.pricing.cleaningFee)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">Platform commission</dt>
                  <dd>{formatCurrency(booking.pricing.platformCommission)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Host payout</dt>
                  <dd>{formatCurrency(booking.pricing.hostPayout)}</dd>
                </div>
                <div className="flex justify-between border-t pt-2 font-semibold text-base">
                  <dt>Total</dt>
                  <dd>{formatCurrency(booking.pricing.totalAmount)}</dd>
                </div>
              </dl>
            </section>
          </div>

          {/* Confirm manual payment */}
          <div className="md:col-span-1">
            <section className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h2 className="font-semibold text-lg mb-1">Confirm payment</h2>
              <p className="text-sm text-gray-500 mb-4">
                For Whish or wire transfers, confirm here once the money has
                actually arrived. This marks the booking paid and records the
                host&apos;s earning.
              </p>

              {isPaid ? (
                <div className="rounded-lg bg-green-50 text-green-800 text-sm p-3">
                  This booking is already paid. Nothing to confirm.
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Method
                    </span>
                    <select
                      value={method}
                      onChange={(e) => setMethod(e.target.value as ManualMethod)}
                      className="w-full rounded-lg border-gray-300 text-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="whish_money">Whish Money</option>
                      <option value="wire_transfer">Wire transfer</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Reference <span className="text-gray-400">(optional)</span>
                    </span>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Transfer / receipt no."
                      className="w-full rounded-lg border-gray-300 text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                  </label>

                  <button
                    onClick={confirmPayment}
                    disabled={submitting}
                    className="w-full py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting
                      ? 'Confirming...'
                      : `Confirm ${formatCurrency(booking.pricing.totalAmount)} received`}
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BookingDetailPage;
